import { request, unwrap } from '../client';
import type {
  ApiResponse,
  AppSlugRenameResponse,
  CreateProductAppRequest,
  ProductAppDto,
  ProductAppDeleteResult,
  SetPrimaryResult,
  UpdateProductAppRequest,
} from '../types';

// Spec 0031 — a product's app portfolio (each app = one Caddy docroot).
// All responses are enveloped → unwrap(). Slug changes use the dedicated endpoint.
const base = (productId: number) => `/api/saas-admin/products/${productId}/apps`;

export const productAppsApi = {
  async list(productId: number): Promise<ProductAppDto[]> {
    const res = await request<ApiResponse<ProductAppDto[]>>('GET', base(productId));
    return unwrap(res);
  },

  async create(productId: number, req: CreateProductAppRequest): Promise<ProductAppDto> {
    const res = await request<ApiResponse<ProductAppDto>>('POST', base(productId), { json: req });
    return unwrap(res);
  },

  async update(
    productId: number,
    appId: number,
    req: UpdateProductAppRequest,
  ): Promise<ProductAppDto> {
    const res = await request<ApiResponse<ProductAppDto>>('PUT', `${base(productId)}/${appId}`, {
      json: req,
    });
    return unwrap(res);
  },

  async renameSlug(productId: number, appId: number, slug: string): Promise<AppSlugRenameResponse> {
    const res = await request<ApiResponse<AppSlugRenameResponse>>(
      'PUT',
      `${base(productId)}/${appId}/slug`,
      { json: { slug } },
    );
    return unwrap(res);
  },

  // SASADM-0178 (SASATH-0395 §2) — `dryRun` previews the move (`data.apexPlan`) and writes nothing.
  // Send it only when the app read carries `setPrimaryDryRun`: an older server does the real call.
  async setPrimary(
    productId: number,
    appId: number,
    opts?: { dryRun?: boolean },
  ): Promise<SetPrimaryResult> {
    const path = `${base(productId)}/${appId}/primary`;
    const res = opts?.dryRun
      ? await request<ApiResponse<SetPrimaryResult>>('PUT', path, { query: { dryRun: 'true' } })
      : await request<ApiResponse<SetPrimaryResult>>('PUT', path);
    return unwrap(res);
  },

  async clearPrimary(productId: number, appId: number): Promise<ProductAppDto> {
    const res = await request<ApiResponse<ProductAppDto>>(
      'DELETE',
      `${base(productId)}/${appId}/primary`,
    );
    return unwrap(res);
  },

  // SASADM-0172 (SASATH-0343 / 0387 §3.1.2) — deleting a PRODUCT-scope app re-renders routing and
  // returns the outcome in `data.routingDeploy`. SASADM-0176 (SASATH-0393 §1) — a TENANT-scope
  // delete returns the removed-hostname / revoked-invitation counts (and its `routingDeploy`);
  // before 0393 it has null data → null.
  async delete(productId: number, appId: number): Promise<ProductAppDeleteResult | null> {
    const res = await request<ApiResponse<ProductAppDeleteResult | null> | undefined>(
      'DELETE',
      `${base(productId)}/${appId}`,
    );
    return res?.data ?? null;
  },
};
